import { useCallback, useEffect, useRef, useState } from 'react';

export interface ResourceState<T> {
  /** Last successfully loaded value (kept across poll failures). */
  data: T | null;
  /** True only while the FIRST load (no data yet) is in flight. */
  loading: boolean;
  /** True while a refetch/poll runs but stale data is still shown. */
  refreshing: boolean;
  /** Set when a load failed. Never coerced into empty data. */
  error: boolean;
  /** Re-run the fetcher. */
  reload: () => void;
}

export interface UseResourceOptions {
  /** Poll interval in ms. Omit for one-shot. On poll failure the last
   *  good snapshot is kept (refreshing/error flags update, data does not). */
  pollMs?: number;
  /** Re-run when any of these change (like a useEffect dep list). */
  deps?: unknown[];
  /** Skip fetching entirely while false (e.g. no id yet). */
  enabled?: boolean;
}

/**
 * The single data-loading primitive for every screen. It exists to kill the
 * project-wide "failure dishonesty" class: unguarded fetches that pin a screen
 * on "טוען…" forever, and catch-blocks that coerce errors into []/null/0 so an
 * outage renders a false "all good".
 *
 * Guarantees:
 *  - loading is ALWAYS cleared (finally), even on throw.
 *  - error is a separate flag — data is NEVER replaced with empty on failure.
 *  - on a poll failure the last good snapshot is kept; only `error`/`refreshing` move.
 *  - a response that resolves after the inputs changed (or after unmount) is
 *    dropped, so a slow stale fetch can't overwrite newer data.
 */
export function useResource<T>(
  fetcher: () => Promise<T>,
  options: UseResourceOptions = {}
): ResourceState<T> {
  const { pollMs, deps = [], enabled = true } = options;

  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(false);

  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const hasData = useRef(false);
  const runId = useRef(0);

  const run = useCallback(async () => {
    const id = ++runId.current;
    if (hasData.current) setRefreshing(true);
    else setLoading(true);
    try {
      const result = await fetcherRef.current();
      if (id !== runId.current) return; // superseded by a newer run / unmounted
      setData(result);
      hasData.current = true;
      setError(false);
    } catch {
      if (id !== runId.current) return;
      // Keep the last good snapshot — only flag the error.
      setError(true);
    } finally {
      if (id === runId.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!enabled) {
      runId.current++; // cancel any in-flight run
      setLoading(false);
      return;
    }
    void run();
    if (pollMs) {
      const t = window.setInterval(() => void run(), pollMs);
      return () => {
        runId.current++;
        window.clearInterval(t);
      };
    }
    return () => { runId.current++; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, pollMs, ...deps]);

  return { data, loading, refreshing, error, reload: run };
}
